import React, { useId } from 'react';
import TypeAhead from '../TypeAhead';
import MultiSelect from '../MultiSelect';
import { titleCase } from '../../utils/titleCase';

/**
 * Drawer field primitives for <TablemaintPage>, extracted verbatim from the
 * Dispatchers page so every table maintenance drawer shares one look:
 *  - required asterisk
 *  - red outline when `invalid`
 *  - the same select / checkbox styling
 *
 * Page access is enforced at the route (PermissionGate), so these use plain
 * inputs rather than the component-permission-aware variants.
 */

export type Option = {
  value: string; label: string; labelSuffix?: string; description?: string;
  /** Under-field selected-info override — a trimmed take on `description`
   *  (e.g. address only, no Sage/AR) for SelectField's showSelectedInfo line. */
  info?: string;
};

/** True for the 'Y' / true / 1 forms a soft-delete flag arrives in. */
export const isDeletedFlag = (v: any): boolean => v === 'Y' || v === true || v === 1;

/** Noun for the deleted-reference note. Lower-cases a normal label ("Customer" ->
 *  "customer") but leaves an acronym alone, so it reads "This SCAC has been deleted."
 *  rather than "This scac has been deleted." */
export const deletedNoun = (label: string): string =>
  label === label.toUpperCase() ? label : label.toLowerCase();

/**
 * SelectField props for an FK whose stored value may point at a since-deleted record.
 *
 * FK option lists return ACTIVE rows only, so a reference to a deleted customer /
 * location / bill-to falls out of the list and would render as a bare recno. These
 * props keep it readable as "Name  ·  #recno" and, when the target really is deleted,
 * explain why underneath. Spread onto a SelectField:
 *
 *   <SelectField {...deletedRefProps('Customer', row.custname, form.customercode, row.custdeleted)} … />
 *
 * The generic drawer wires this from the registry's refNameKey / deletedKey; bespoke
 * drawers call it directly with their own record fields.
 */
export const deletedRefProps = (
  label: string,
  refName: any,
  recno: any,
  deletedFlag: any,
): { invalidLabel?: string; invalidNote?: string } => {
  const name = String(refName ?? '').trim();
  const id = String(recno ?? '').trim();
  return {
    invalidLabel: name ? `${titleCase(name)}  ·  #${id}` : undefined,
    invalidNote: isDeletedFlag(deletedFlag) ? `This ${deletedNoun(label)} has been deleted.` : undefined,
  };
};

/** Normalize a registry `options` list (strings or {value,label}) for a select. */
export const normalizeOptions = (
  options: (string | Option)[] | undefined,
): Option[] =>
  (options ?? []).map((o) =>
    typeof o === 'string' ? { value: o, label: o === '' ? '— None —' : o } : o,
  );

/** Yes/No pill for boolean-ish ('Y'/true/1) table columns. */
export const BoolPill: React.FC<{ value: boolean }> = ({ value }) => (
  <span
    className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
      value
        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
    }`}
  >
    {value ? 'Yes' : 'No'}
  </span>
);

export const Toggle: React.FC<{
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  hint?: string;
}> = ({ label, checked, onChange, disabled, hint }) => (
  <label className="flex items-start gap-3 cursor-pointer select-none py-1">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
    />
    <span className="text-sm">
      <span className="font-medium text-label">{label}</span>
      {hint && <span className="block text-xs text-muted">{hint}</span>}
    </span>
  </label>
);

export const SelectField: React.FC<{
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: Option[];
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  /** Allow clearing to blank (default true). Set false for must-have-a-value selects. */
  clearable?: boolean;
  /** Keep a value that isn't in `options` visible (flagged) rather than dropping it. */
  preserveUnknown?: boolean;
  /** Display text for a preserved unknown value (e.g. the deleted entity's name
   *  resolved by the caller) instead of the raw stored value/recno. */
  invalidLabel?: string;
  /** Red note rendered UNDER the field while the current value is unknown to
   *  `options` (e.g. "This customer has been deleted."). */
  invalidNote?: string;
  /** Render each option's `description` (e.g. address + recno) as a second line. */
  showDescriptions?: boolean;
  /** Suppress the field's own label (for table-style rows where the label is a shared
   *  column header rendered once above the rows). */
  hideLabel?: boolean;
  /** Dense variant (forwarded to TypeAhead) for grid/table cells. */
  compact?: boolean;
  /** Hide the in-input search icon (default shown) — reclaims text space in
   *  narrow grid cells where the icon clips short values like 'ALL'. */
  showSearch?: boolean;
  /** Render the SELECTED option's description (address etc.) as a muted line
   *  under the field — the picked record's info without opening the dropdown. */
  showSelectedInfo?: boolean;
  /** A combobox that accepts a newly typed value as well as a listed option. */
  allowNewOptions?: boolean;
}> = ({ label, value, onChange, options, disabled, required, clearable = true, preserveUnknown = true, invalidLabel, invalidNote, showDescriptions = false, hideLabel = false, compact, showSearch = true, showSelectedInfo = false, allowNewOptions = false }) => {
  const selected = value ? options.find((o) => o.value === value) : undefined;
  // Mirrors TypeAhead's isInvalidValue (options.length guard avoids flashing
  // the note while async options are still loading).
  const isUnknown = !!value && options.length > 0 && !selected;
  return (
    <div>
      {!hideLabel && (
        <label className="block text-sm font-medium text-label mb-1">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}
      <TypeAhead
        options={options}
        value={value}
        onChange={onChange}
        freeText={allowNewOptions}
        onClear={() => onChange('')}
        allowClear={clearable}
        placeholder="Select…"
        disabled={disabled}
        showSearch={showSearch}
        showDescriptions={showDescriptions}
        flagInvalidValue={preserveUnknown}
        invalidValueLabel={invalidLabel}
        // The under-field info line carries the id — skip the in-field one.
        showValueSuffix={!showSelectedInfo}
        compact={compact}
      />
      {invalidNote && isUnknown && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{invalidNote}</p>
      )}
      {showSelectedInfo && selected && (selected.labelSuffix || selected.info || selected.description) && (
        <p className="mt-1 text-xs text-muted">
          {selected.labelSuffix && (
            <span className="mr-2 font-semibold text-green-700 dark:text-green-400">
              {selected.labelSuffix}
            </span>
          )}
          {selected.info ?? selected.description}
        </p>
      )}
    </div>
  );
};

export const TextField: React.FC<{
  label: string;
  value: string;
  onChange: (next: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  type?: string;
  maxLength?: number;
  invalid?: boolean;
}> = ({ label, value, onChange, onBlur, disabled, required, placeholder, type = 'text', maxLength, invalid }) => {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-label mb-1">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 dark:bg-gray-700 dark:text-white ${
          invalid
            ? 'border-red-400 dark:border-red-500 focus:ring-red-500'
            : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
        }`}
      />
    </div>
  );
};

/** Multi-select field (e.g. dispatcher-group members, repair-category equipment
 *  types). Value is a string[]; spans the full drawer width. Options are static
 *  or fetched from an `optionsEndpoint`. */
export const MultiSelectField: React.FC<{
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  options: Option[];
  disabled?: boolean;
  required?: boolean;
  loading?: boolean;
  placeholder?: string;
}> = ({ label, value, onChange, options, disabled, required, loading, placeholder }) => (
  <div className="md:col-span-2">
    <label className="block text-sm font-medium text-label mb-1">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    <MultiSelect
      options={options}
      value={Array.isArray(value) ? value : []}
      onChange={onChange}
      disabled={disabled}
      loading={loading}
      enableSearch
      usePortal
      placeholder={placeholder ?? 'Select…'}
    />
  </div>
);

/** Multi-line text field — spans the full drawer width (both grid columns) for
 *  long values like a PDF message. */
export const TextAreaField: React.FC<{
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  rows?: number;
}> = ({ label, value, onChange, disabled, required, invalid, rows = 8 }) => {
  const id = useId();
  return (
    <div className="md:col-span-2">
      <label htmlFor={id} className="block text-sm font-medium text-label mb-1">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={rows}
        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 dark:bg-gray-700 dark:text-white ${
          invalid
            ? 'border-red-400 dark:border-red-500 focus:ring-red-500'
            : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
        }`}
      />
    </div>
  );
};
